import { Controller } from "@hotwired/stimulus";
import { flashQuantity, cancelQuantityFlash } from "../utils/quantity_flash";

export default class extends Controller {
  static targets = ["input", "quantityInput", "quantityDisplay", "decrementButton", "incrementButton"];
  static values = {
    uniqueChoice: Boolean,
    obligatory: Boolean,
    maxQuantity: { type: Number, default: 1 }
  };

  select({ target: input }) {
    if (input.checked) {
      if (this.uniqueChoiceValue) this.#deselectSiblings(input);
      return;
    }

    if (!this.obligatoryValue || this.inputTargets.some((other) => other.checked)) return;

    input.checked = true;
  }

  increment({ params: { accessoryId } }) {
    this.#step(accessoryId, 1);
  }

  decrement({ params: { accessoryId } }) {
    this.#step(accessoryId, -1);
  }

  applyCaps() {
    this.quantityInputTargets.forEach((input) => {
      this.#writeQuantity(input.dataset.accessoryId, Number(input.value) || 1);
    });
  }

  disconnect() {
    this.quantityDisplayTargets.forEach(cancelQuantityFlash);
  }

  #step(accessoryId, delta) {
    const input = this.#targetFor(this.quantityInputTargets, accessoryId);
    if (!input) return;

    const current = Number(input.value) || 1;

    const quantity = Math.min(Math.max(current + delta, 1), Math.max(this.#maxFor(input), current));
    if (quantity === current) return;

    this.#writeQuantity(accessoryId, quantity);

    flashQuantity(this.#targetFor(this.quantityDisplayTargets, accessoryId));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  #maxFor(input) {
    return Math.min(this.maxQuantityValue, Number(input.dataset.maxQuantity ?? Infinity));
  }

  #deselectSiblings(input) {
    this.inputTargets
      .filter((other) => other !== input && other.checked)
      .forEach((other) => {
        other.checked = false;
        this.#writeQuantity(other.dataset.accessoryId, 1);
      });
  }

  #writeQuantity(accessoryId, quantity) {
    const input = this.#targetFor(this.quantityInputTargets, accessoryId);
    if (!input) return;

    input.value = quantity;

    const display = this.#targetFor(this.quantityDisplayTargets, accessoryId);
    if (display) display.textContent = quantity;

    const max = this.#maxFor(input);
    const buttons = {
      decrementButton: quantity <= 1,
      incrementButton: quantity >= max
    };
    Object.entries(buttons).forEach(([name, disabled]) => {
      const button = this.#targetFor(this[`${name}Targets`], accessoryId);
      if (button) button.disabled = disabled;
    });
  }

  #targetFor(targets, accessoryId) {
    return targets.find((target) => target.dataset.accessoryId === String(accessoryId));
  }
}
